import { useEffect, useState } from "react";
import PageHeading from "../../components/PageElements/PageHeading";

import Table from "../../components/Tables/Table";
import { Form, Modal, Space, Tag, Tooltip } from "antd";
import { FiEdit, FiTrash2 } from "react-icons/fi";
import toast from "react-hot-toast";
import TableHeader from "../../components/Tables/TableHeader";
import TextInput from "../../components/Inputs/TextInput";
import FormButton from "../../components/Buttons/FormButton";
import { FieldArray, Formik } from "formik";
import FormCancelButton from "../../components/Buttons/FormCancelButton";
import FormErrors from "../../components/Errors/FormErrors";
import DeleteModal from "../../components/Modals/DeleteModal";
import { IoIosWarning } from "react-icons/io";
import {
  createAccreditingBody,
  deleteAccreditingBody,
  getAccreditingBodies,
  updateAccreditingBody,
} from "../../services/accreditingBody";
import { accreditingBodySchema } from "../../validators/accreditingBodyValidators";
import DynamicTextInput from "../../components/Inputs/DynamicTextInput";

const AccreditingBody = () => {
  const [accreditingBodies, setAccreditingBodies] = useState([]);
  const [filteredAccreditingBodies, setFilteredAccreditingBodies] = useState(
    []
  );
  const [searchTerm, setSearchTerm] = useState("");
  const [loading, setLoading] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [accreditingBodyToEdit, setAccreditingBodyToEdit] = useState(null);
  const [modalText, setModalText] = useState("");
  const [confirmLoading, setConfirmLoading] = useState(false);
  const [accreditingBodyToDelete, setAccreditingBodyToDelete] = useState(null);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [errs, setErrs] = useState(null);

  useEffect(() => {
    loadAccreditingBodies();
  }, []);

  useEffect(() => {
    const debounce = setTimeout(() => {
      const filtered = accreditingBodies.filter((item) =>
        item.name?.toLowerCase().includes(searchTerm.toLowerCase())
      );
      setFilteredAccreditingBodies(filtered);
    }, 300);

    return () => clearTimeout(debounce);
  }, [searchTerm, accreditingBodies]);

  const loadAccreditingBodies = async () => {
    setLoading(true);
    try {
      const { data } = await getAccreditingBodies();
      setAccreditingBodies(data.data);
      setFilteredAccreditingBodies(data.data);
    } catch (error) {
      toast.error(
        error?.response?.data?.error || "Failed to load Accrediting Bodies"
      );
    } finally {
      setLoading(false);
    }
  };

  const onSubmit = (payload, resetForm) => {
    if (accreditingBodyToEdit) {
      onUpdateAccreditingBody(payload, resetForm);
    } else {
      const nonEmptyAccreditingBodies = payload?.accreditingBodies.filter(
        (accreditingBody) => accreditingBody?.name !== ""
      );

      if (nonEmptyAccreditingBodies.length <= 0) {
        toast.error("Accrediting Bodies cannot be empty!");

        return;
      }
      onCreateAccreditingBodies(nonEmptyAccreditingBodies, resetForm);
    }
  };

  const onCreateAccreditingBodies = async (
    accreditingBodiesArray,
    resetForm
  ) => {
    setErrs(null);

    try {
      const promises = accreditingBodiesArray.map(async (accreditingBody) => {
        const payload = { name: accreditingBody.name };

        const { data } = await createAccreditingBody(payload);

        return data;
      });

      // Wait for all the createDepartment requests to complete
      const createdAccreditingBodies = await Promise.all(promises);

      setAccreditingBodies((prev) => [...prev, ...createdAccreditingBodies]);

      toast.success("Accrediting body(ies) created successfully");

      setIsModalOpen(false);

      resetForm({ values: { accreditingBodies: [{ name: "" }] } });

      await loadAccreditingBodies();
    } catch (error) {
      const { response } = error;
      if (response && response.status === 422) {
        setErrs(response.data.errors);
      } else {
        toast.error(
          response.data.error ||
            "Some Accrediting Bodies were not created, try again!"
        );
      }
    }
  };

  const handleEdit = (accreditingBody) => {
    setAccreditingBodyToEdit(accreditingBody);
    setIsModalOpen(true);
  };

  const onUpdateAccreditingBody = async (payload, resetForm) => {
    setErrs(null);

    try {
      const originalAccreditingBodies = [...accreditingBodies];

      const accreditingBodyIndex = originalAccreditingBodies.findIndex(
        (accreditingBody) => accreditingBody.id === accreditingBodyToEdit.id
      );

      if (accreditingBodyIndex === -1)
        throw new Error("Accrediting Body not found.");

      const { data } = await updateAccreditingBody(
        accreditingBodyToEdit.id,
        payload
      );

      originalAccreditingBodies[accreditingBodyIndex] = data.data;

      setAccreditingBodies(originalAccreditingBodies);

      setAccreditingBodyToEdit(null);

      resetForm();

      setIsModalOpen(false);

      toast.success("Accrediting Body updated successfully!");
    } catch (error) {
      const { response } = error;
      if (response && response.status === 422) {
        setErrs(response.data.errors);
      } else {
        toast.error(
          response?.data?.error || "Accrediting Body not updated, try again!"
        );
      }
    }
  };

  const handleDelete = (payload) => {
    setModalText(
      `Please confirm that you want to delete %${payload?.name?.toUpperCase()}`
    );
    setAccreditingBodyToDelete(payload);
    setIsDeleteModalOpen(true);
  };

  const onDeleteAccreditingBody = async () => {
    const originalAccreditingBodies = [...accreditingBodies];

    const newAccreditingBodies = originalAccreditingBodies.filter(
      (accreditingBody) => accreditingBody.id !== accreditingBodyToDelete.id
    );

    setAccreditingBodies(newAccreditingBodies);

    try {
      await deleteAccreditingBody(accreditingBodyToDelete?.id);

      toast.success("Accrediting Body deleted successfully!");
    } catch (error) {
      const { response } = error;
      toast.error(
        ` ${
          response?.data?.message ||
          error?.response?.data?.error ||
          "Accrediting Body not deleted, try again later"
        }`
      );

      setAccreditingBodies(originalAccreditingBodies);
    } finally {
      setIsDeleteModalOpen(false);

      setAccreditingBodyToDelete(null);
    }
  };

  const handleCancel = () => {
    setIsModalOpen(false);
    setIsDeleteModalOpen(false);
    setAccreditingBodyToEdit(null);
    setAccreditingBodyToDelete(null);
    setErrs(null);
  };

  const columns = [
    {
      title: "#",
      render: (text, record, index) => index + 1,
      width: 500,
    },
    {
      title: "Name",
      dataIndex: "name",
      key: "name",
    },

    {
      title: "Action",
      key: "action",
      width: 160,
      render: (_, record) => (
        <Space size="middle">
          <Tooltip title="Edit Accrediting Body">
            <button
              className="p-2 border border-green-200 rounded-md text-green-600 hover:bg-green-50 transition cursor-pointer"
              onClick={() => handleEdit(record)}
            >
              <FiEdit size={16} />
            </button>
          </Tooltip>
          <Tooltip title="Delete Accrediting Body">
            <button
              className="p-2 border border-red-200 rounded-md text-red-500 hover:bg-red-50 transition cursor-pointer"
              onClick={() => handleDelete(record)}
            >
              <FiTrash2 size={16} />
            </button>
          </Tooltip>
        </Space>
      ),
    },
  ];

  return (
    <>
      <PageHeading
        title="Accrediting Bodies"
        subtitle1="Institution"
        subtitle2="Accrediting Bodies"
      />

      <div className="px-8 py-6">
        <div className="bg-white shadow-sm rounded-lg p-6">
          <TableHeader
            title="Accrediting Bodies List"
            count={filteredAccreditingBodies.length}
            searchTerm={searchTerm}
            setSearchTerm={setSearchTerm}
            buttonLabel="+ Add Accrediting Body"
            onButtonClick={() => setIsModalOpen(true)}
          />

          <Table
            isPaginable
            pageSize={10}
            isSearchable={false}
            loading={loading}
            columns={columns}
            rows={filteredAccreditingBodies.map((item) => ({
              ...item,
              key: item.id,
            }))}
          />

          <Modal
            title={
              <div className="px-4 py-3">
                <h2 className="text-lg font-semibold text-gray-700">
                  {accreditingBodyToEdit
                    ? "Edit Accrediting Body"
                    : "Add Accrediting Body"}
                </h2>
              </div>
            }
            open={isModalOpen}
            onCancel={handleCancel}
            footer={false}
          >
            {accreditingBodyToEdit ? (
              <Formik
                initialValues={{
                  name: accreditingBodyToEdit
                    ? accreditingBodyToEdit?.name
                    : "",
                }}
                enableReinitialize
                validationSchema={accreditingBodySchema}
                onSubmit={(values, { resetForm }) => {
                  onSubmit(values, resetForm);
                }}
              >
                {({ values, handleChange, handleBlur, errors, submitForm }) => {
                  return (
                    <Form className="space-y-5 px-4! py-4!">
                      {errs && <FormErrors errors={errs} />}

                      <TextInput
                        name="name"
                        title="Name"
                        placeholder="Enter Accrediting Body"
                        requiredField={true}
                        errors={errors}
                        values={values}
                        handleBlur={handleBlur}
                        handleChange={handleChange}
                        autoFocus
                      />

                      <div className="flex justify-end gap-2">
                        <FormCancelButton
                          text="Cancel"
                          onClick={handleCancel}
                        />
                        <FormButton
                          text="Save"
                          type="primary"
                          onClick={submitForm}
                        />
                      </div>
                    </Form>
                  );
                }}
              </Formik>
            ) : (
              <Formik
                initialValues={{
                  accreditingBodies: [{ name: "" }],
                }}
                enableReinitialize
                onSubmit={(values, { resetForm }) => {
                  onSubmit(values, resetForm);
                }}
              >
                {({ values, handleChange, handleBlur, errors, submitForm }) => (
                  <Form className="space-y-2 !px-5 pb-3">
                    {errs && <FormErrors errors={errs} />}
                    <FieldArray name="accreditingBodies">
                      {({ remove, push }) =>
                        (values.accreditingBodies || []).map(
                          (accreditingBody, index) => (
                            <DynamicTextInput
                              key={index}
                              index={index}
                              name={`accreditingBodies.${index}.name`}
                              placeholder="Enter Accrediting Body Name"
                              requiredField={true}
                              errors={errors}
                              values={values}
                              handleBlur={handleBlur}
                              handleChange={handleChange}
                              push={push}
                              remove={remove}
                              pushName="accreditingBodies"
                              dynamicFieldMap={{
                                auditCategories: {
                                  name: "",
                                },
                              }}
                            />
                          )
                        )
                      }
                    </FieldArray>
                    <div className="flex justify-end gap-2">
                      <FormCancelButton text="Cancel" onClick={handleCancel} />
                      <FormButton
                        text="Save"
                        type="primary"
                        onClick={submitForm}
                      />
                    </div>
                  </Form>
                )}
              </Formik>
            )}
          </Modal>

          <DeleteModal
            open={isDeleteModalOpen}
            handleOk={onDeleteAccreditingBody}
            handleCancel={handleCancel}
            confirmLoading={confirmLoading}
            modalText={modalText}
            extra={
              <div className="mt-3 bg-red-100 text-red-500 border-[1px] border-red-300 mx-5 px-3 py-2 rounded-md">
                <h3 className="text-red-700 text-md flex gap-1 items-center">
                  <IoIosWarning /> Warning
                </h3>
                Please note that this action cannot be reversed!
              </div>
            }
          />
        </div>
      </div>
    </>
  );
};

export default AccreditingBody;
